/**
 * D188 — the appearance preference, and the reversal of D179 (b)'s *"there is no toggle, and the
 * absence is the decision."*
 *
 * **Two values, deliberately not one.** What the resident chooses is a `ColorSchemePreference`
 * (`auto | light | dark`); what the stylesheet reads is a *resolved* `light | dark` stamped on
 * `<html data-scheme>`. Collapsing them loses the ability to say *follow the system*, and
 * resolving in CSS instead loses the ability to override it — `prefers-color-scheme` has no
 * "ignore me" value.
 *
 * **Why the attribute rather than a media query.** `global.css` carried four
 * `@media (prefers-color-scheme: dark)` blocks — the palette delta, the four lighting exceptions,
 * the desktop bezel shadow, and the gate-open overlay's weights. Keeping the media queries and
 * adding `[data-scheme="dark"]` twins beside them would have duplicated every one of those blocks
 * and minted four places for the two halves to drift apart. So the attribute is the *only*
 * mechanism: the media query is consulted once, here, and never in the stylesheet.
 * `theme.test.tsx` fails on a `@media (prefers-color-scheme: dark)` returning to `global.css`,
 * because such a block is a scheme rule the preference cannot reach — green in CI and dead for
 * every resident who has chosen a scheme.
 *
 * **The accepted cost is that the scheme is now JS-owned.** Before the boot script in
 * `index.html` runs, `:root` is the light palette. That script is inline and render-blocking in
 * `<head>`, so the window is closed in practice, and an app that cannot render without JS at all
 * loses nothing further — but it is a real change from a stylesheet that was correct with no
 * script running, and it is D188's price rather than an oversight.
 *
 * **Why `localStorage` and not `PATCH /users/me`.** The same argument
 * `screens/home/layoutPreference.ts` makes at length, and it is stronger here: appearance is a
 * property of *this handset in this light*, so a phone in dark and a desktop in light is the
 * correct outcome rather than a conflict to reconcile. Unlike `locale` there is no field for it,
 * and minting an account-level one is a PRD decision. `localStorage` is also the only store the
 * boot script can read **synchronously before the first paint**, which is the whole point:
 * an async read paints the wrong scheme and then corrects it.
 */

import { STAGE } from '../auth/config'

export type ColorSchemePreference = 'auto' | 'light' | 'dark'

/** What the stylesheet reads. `auto` is not one of them — it is resolved before it gets here. */
export type ResolvedColorScheme = 'light' | 'dark'

export const DEFAULT_COLOR_SCHEME: ColorSchemePreference = 'dark'

/**
 * The three options in the order the preferences pill lays them out, light → auto → dark: the
 * scale runs one way, and `auto` sits between the two it chooses between rather than at an end.
 */
export const COLOR_SCHEME_OPTIONS: readonly ColorSchemePreference[] = ['light', 'auto', 'dark']

const DARK_QUERY = '(prefers-color-scheme: dark)'

const schemeKey = (stage: string): string => `selogate.${stage}.color-scheme`

function isPreference(value: unknown): value is ColorSchemePreference {
  return value === 'auto' || value === 'light' || value === 'dark'
}

/**
 * The stored preference, or the default. **Never throws** — private mode and a storage-disabled
 * browser *throw* on `getItem` rather than returning `null` (`auth/credentials.ts`'s rule), and an
 * appearance preference is a convenience whose absence costs a tap.
 *
 * A value that is not one of the three falls back rather than reaching the DOM: an unknown string
 * stamped into `data-scheme` matches no rule, which renders the light palette while the pill shows
 * a selection nothing is selected.
 */
export function readColorScheme(stage: string = STAGE): ColorSchemePreference {
  try {
    const raw = globalThis.localStorage?.getItem(schemeKey(stage))
    return isPreference(raw) ? raw : DEFAULT_COLOR_SCHEME
  } catch {
    return DEFAULT_COLOR_SCHEME
  }
}

export function writeColorScheme(next: ColorSchemePreference, stage: string = STAGE): void {
  try {
    globalThis.localStorage?.setItem(schemeKey(stage), next)
  } catch {
    /* ignore — see readColorScheme */
  }
}

/** `auto` asks the platform; the other two answer for themselves. */
export function resolveColorScheme(preference: ColorSchemePreference): ResolvedColorScheme {
  if (preference !== 'auto') return preference
  try {
    return globalThis.matchMedia?.(DARK_QUERY).matches === true ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

/**
 * Stamps the resolved scheme on `<html>` and brings the browser chrome with it.
 *
 * **The `theme-color` sync carries no hex literal, and that is deliberate.** `index.html`'s two
 * tags stay media-qualified and stay exactly as D179 wrote them; this writes the *same* resolved
 * `--g0` into both, so whichever `media` the UA matches, the value it reads is the scheme the
 * resident chose. Restating `#fcf7ef`/`#131519` here would be a third and fourth copy of a colour
 * the palette already owns, kept in step by nobody.
 *
 * It reads the computed value rather than the token module because that is what actually landed:
 * a `--g0` the stylesheet failed to apply is a `--g0` the edges do not have, and the bar would
 * then disagree with the page it sits on — which is the one thing D132 exists to prevent.
 */
export function applyColorScheme(preference: ColorSchemePreference): void {
  const resolved = resolveColorScheme(preference)
  const root = globalThis.document?.documentElement
  if (root === undefined) return
  root.dataset['scheme'] = resolved

  const surface = globalThis.getComputedStyle(root).getPropertyValue('--g0').trim()
  if (surface === '') return
  for (const tag of globalThis.document.querySelectorAll('meta[name="theme-color"]')) {
    tag.setAttribute('content', surface)
  }
}

/**
 * The boot call, from `main.tsx` and outside React for the reason the four calls beside it are:
 * it is a property of the document, it must be right on the first paint, and it has no state a
 * component would own. Never torn down.
 *
 * It is **not** redundant with `index.html`'s inline script, which runs before the stylesheet has
 * been applied and so can only stamp the attribute. This does the two things that need a live
 * page: syncs `theme-color` from the computed palette, and keeps `auto` following the platform
 * when the OS crosses sunset with the app already open.
 */
export function installColorScheme(): void {
  applyColorScheme(readColorScheme())

  const query = globalThis.matchMedia?.(DARK_QUERY)
  if (query === undefined) return
  query.addEventListener('change', () => {
    // Re-read rather than close over the preference: the pill writes the key, so a resident who
    // switched to `light` and then watched the OS go dark must not be dragged back by this.
    if (readColorScheme() === 'auto') applyColorScheme('auto')
  })
}
